import { HttpClient, HttpContext } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { TranslateLoader, TranslateService, TranslationObject } from '@ngx-translate/core';
import { Observable, of } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import { SILENT_REQUEST } from '../http-timeout.interceptor';
import { BUNDLED_TRANSLATIONS } from './i18n-bundle';

/**
 * Zero-lag TranslateLoader with background CDN revalidation.
 *
 * Startup path (always synchronous, zero network wait):
 *   1. Check localStorage for a CDN-refreshed version from a previous session.
 *   2. Fall back to the statically-bundled copy (compiled into the JS output).
 *   3. Return of(immediate) — synchronous, no HTTP, no splash-screen lag.
 *
 * Background path (after render, fire-and-forget, AT MOST ONCE PER LANGUAGE
 * PER APP SESSION — subsequent translation loads/switches within the same
 * running app instance are served entirely from localStorage, no network):
 *   4. Fetch latest translations from CDN.
 *   5. If they differ, update localStorage and call reloadLang() silently.
 *      The UI re-renders in place — no spinner, no visible flash.
 *
 * CDN path:   <cdnBaseUrl>/i18n/<lang>.json
 * Cache key:  _i18n_<lang>  (localStorage)
 */
@Injectable()
export class CdnTranslateLoader implements TranslateLoader {
  private readonly cdnPrefix = `${environment.cdnBaseUrl}/i18n/`;
  private readonly suffix = '.json';

  /** Set by AppComponent after construction so reloadLang() can be triggered. */
  translateService?: TranslateService;

  /** Langs already revalidated against the CDN this app session — prevents
   *  re-fetching on every language switch/reload within the same run. */
  private readonly revalidatedThisSession = new Set<string>();

  constructor(private readonly http: HttpClient) {}

  getTranslation(lang: string): Observable<TranslationObject> {
    const safeLang = this.sanitiseLang(lang);

    // The bundle is always the authoritative base — it contains every key that
    // shipped with this build (including newly-added keys like SPENDING).
    const bundled: TranslationObject = BUNDLED_TRANSLATIONS[safeLang] ?? BUNDLED_TRANSLATIONS['en'];
    // Prefer a CDN-refreshed copy cached from the previous session, but merge
    // it ON TOP of the bundle so new keys are never missing from a stale cache.
    // Must be a DEEP merge — a shallow {...bundled, ...cached} would let a stale
    // cached "WELCOME" (or any other namespace) object entirely replace the fresh
    // bundled one, silently hiding any key added since that cache was written.
    const cached = this.readCache(safeLang);
    const immediate: TranslationObject = cached ? this.deepMerge(bundled, cached) : bundled;

    // Revalidate CDN after render — never blocks startup.
    this.revalidateInBackground(safeLang, cached);

    // Synchronous emit: translations are available before the first paint.
    return of(immediate);
  }

  // ── Private helpers ──────────────────────────────────────────────────────

  /** Recursively merges overlay onto base — a key present only in base (e.g. one newly added to the bundle) survives even when overlay has an older sibling object at the same path. */
  private deepMerge(base: TranslationObject, overlay: TranslationObject): TranslationObject {
    const result: TranslationObject = { ...base };
    for (const key of Object.keys(overlay)) {
      const overlayValue = overlay[key];
      const baseValue = result[key];
      result[key] = this.isPlainObject(overlayValue) && this.isPlainObject(baseValue)
        ? this.deepMerge(baseValue as TranslationObject, overlayValue as TranslationObject)
        : overlayValue;
    }
    return result;
  }

  private isPlainObject(value: unknown): value is TranslationObject {
    return typeof value === 'object' && value !== null && !Array.isArray(value);
  }

  private revalidateInBackground(lang: string, cached: TranslationObject | null): void {
    // Only hit the CDN once per language per app session — every subsequent
    // getTranslation()/reloadLang() call for a lang already checked this run
    // (language switches, preference reconciliation, etc.) is served from the
    // localStorage cache written below, with zero further network calls.
    if (this.revalidatedThisSession.has(lang)) return;
    this.revalidatedThisSession.add(lang);

    this.http
      .get<TranslationObject>(`${this.cdnPrefix}${lang}${this.suffix}`, {
        context: new HttpContext().set(SILENT_REQUEST, true),
      })
      .pipe(catchError(() => of(null)))
      .subscribe(fresh => {
        if (!fresh) return;

        // Compare against the raw cached CDN copy, NOT the bundle-merged value —
        // the merged value is deliberately always a superset of the CDN response
        // (see getTranslation()), so comparing against it here would always look
        // "different" and cause reloadLang() to re-enter getTranslation(), which
        // calls revalidateInBackground() again — an infinite request loop.
        if (cached && JSON.stringify(fresh) === JSON.stringify(cached)) return;

        this.writeCache(lang, fresh);
        this.translateService?.reloadLang(lang).subscribe();
        console.info(`[i18n] CDN update applied silently for "${lang}".`);
      });
  }

  private readCache(lang: string): TranslationObject | null {
    try {
      const raw = localStorage.getItem(`_i18n_${lang}`);
      return raw ? (JSON.parse(raw) as TranslationObject) : null;
    } catch {
      return null;
    }
  }

  private writeCache(lang: string, data: TranslationObject): void {
    try {
      localStorage.setItem(`_i18n_${lang}`, JSON.stringify(data));
    } catch {
      // localStorage full or unavailable — not fatal.
    }
  }

  /** Strip anything that isn't a safe BCP 47 subtag character to prevent path traversal. */
  private sanitiseLang(lang: string): string {
    return lang.replace(/[^a-zA-Z0-9-]/g, '').toLowerCase();
  }
}
